import Table from "atoms/Table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import queryKeys from "common/queryKey";
import moment from "moment";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import { DatePicker, message, Form, Row, Col } from "antd";
import { dateInDetail, getMySqlDate } from "utils/Date";
import { connect } from "react-redux";
import { getUserList } from "redux/selectors";
import { find, reduce, uniqBy } from "lodash";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { CSVLink } from "react-csv";
import ViewMore from "organisms/ViewMore";
import styled from "styled-components";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

const StyledButton = styled(ButtonOutlined)`
  width: auto; /* Default width */

  @media (max-width: 320px) {
    width: 90%; /* Adjust width for mobile */
  }
  @media (max-width: 375px) {
    margin-top: -2px !important;
  }
  @media (max-width: 425px) {
    margin-top: -2px !important;
  }
`;

const CSVHeaders = [
  { label: "Action Type" },
  { label: "Date" },
  { label: "Name" },
  { label: "Action Log" },
];

const { RangePicker } = DatePicker;

function ActivityLog({ userList }) {
  const resourceColumns = [
    {
      title: " Action Type ",
      dataIndex: "actionType",
      key: "actionType",
    },
    {
      title: "Action Date",
      dataIndex: "actionDate",
      key: "actionDate",
      render: (date) => {
        const dateFormat = moment.utc(date).format("DD-MM-YYYY / HH:mm:ss");
        return `${dateFormat}`;
      },
    },
    {
      title: "Action By",
      dataIndex: "userName",
      key: "userName",
      render: (_, record) => {
        const user = find(userList, { userid: record?.actionBy });
        let name =
          record?.actionBy && record?.userType === "Owner"
            ? record?.userName
            : user
            ? user?.fullname
            : "-";
        return name;
      },
    },
    {
      title: "Logs",
      dataIndex: "actionLog",
      key: "actionLog",
      width: "40%",
      render: (actionLog) => <ViewMore value={actionLog} count={40} />,
    },
  ];
  const csvLinkEl = useRef();

  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [queryParams, setQueryParams] = useState("");
  const [CSVreportlist, setCSVreportlist] = useState([]);

  const handleDateChange = (date, dateString) => {
    setSelectedDateRange(date);
  };

  const handleFilterChange = () => {
    const values = form.getFieldsValue();
    setSelectedFilter(values.actionType || []);
  };

  const handleUserChange = () => {
    const values = form.getFieldsValue();
    setSelectedUser(values.actionBy || []);
  };

  const { getActivityLog } = queryKeys;

  const getQueryParams = () => {
    let startDate, endDate;
    if (selectedDateRange && selectedDateRange.length === 2) {
      [startDate, endDate] = selectedDateRange;
    }

    const queryParams = new URLSearchParams();
    queryParams.append("actionType", selectedFilter.join(",") || "");

    if (startDate && endDate) {
      let rangeEnd = getMySqlDate(endDate);
      let rangeStart = getMySqlDate(startDate);
      queryParams.append("startDate", rangeStart);
      queryParams.append("endDate", rangeEnd);
    } else {
      queryParams.append("startDate", "");
      queryParams.append("endDate", "");
    }

    queryParams.append("actionBy", selectedUser.join(",") || "");

    const queryString = queryParams.toString();
    setQueryParams(`?${queryString}`);
  };

  useEffect(() => {
    getQueryParams();
  }, [selectedFilter, selectedDateRange, selectedUser]);

  const { key, url } = getActivityLog(queryParams);

  const { data, lastElementRef, isLoading } = useScrollFetch2(key, {
    endPoint: `${url}${queryParams}`, // Append queryParams to the URL
    queryParam: "lastEvaluatedKey",
  });

  const activitylog = useMemo(() => {
    return data?.pages?.reduce((acc, page) => {
      if (page?.response?.data === undefined) return acc;

      return [...acc, ...page?.response?.data];
    }, []);
  }, [data]);

  const [form] = Form.useForm();
  const prepareCSV = async () => {
    if (activitylog.length > 0) {
      setCSVreportlist(
        reduce(
          activitylog,
          function (result, record) {
            const user = find(userList, { userid: record?.actionBy });
            result.push({
              "Action Type": record?.actionType,
              Date: moment
                .utc(record.actionDate)
                .format("DD-MM-YYYY / HH:mm:ss"),
              Name:
                record?.actionBy && record?.userType === "Owner"
                  ? record?.userName
                  : user
                  ? user?.fullname
                  : "-",
              "Action Log": record.actionLog,
            });
            return result;
          },
          []
        )
      );
      setTimeout(() => {
        csvLinkEl.current.link.click();
      });
    } else {
      message.error(`No data available to download.`);
    }
  };

  const uniqueActionTypes = [
    { label: "All", value: "" },
    { label: "Edited", value: "Edited" },
    { label: "Created", value: "Created" },
    { label: "Deleted", value: "Deleted" },
  ];

  const uniqueEmployeeNames = uniqBy(
    userList?.map((user) => ({
      label: user.fullname,
      value: user.userid,
    })),
    "value"
  );

  return (
    <div>
      <div className="w-100 d-flex justify-content-between">
        <h5>Activity Log</h5>
        <div className="text-end">
          <StyledButton
            onClick={prepareCSV}
            disabled={activitylog?.length === 0 || null || undefined}
          >
            Export CSV file
          </StyledButton>
          <CSVLink
            header={CSVHeaders}
            data={CSVreportlist}
            filename={"activityLog.csv"}
            asyncOnClick={true}
            ref={csvLinkEl}
          />
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        className="mt-2"
        onValuesChange={() => {
          handleFilterChange();
          handleUserChange();
        }}
        initialValues={{ actionType: [], actionBy: [] }}
      >
        <Row gutter={12} className="g-3 mb-2">
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <DropdownCheckBox
              name="actionType"
              label="Select Action Type"
              options={uniqueActionTypes}
              allowClear={true}
              form={form}
              handleFormChange={handleFilterChange}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <DropdownCheckBox
              name="actionBy"
              label="Filter by Employee"
              options={uniqueEmployeeNames}
              allowClear={true}
              form={form}
              handleFormChange={handleUserChange}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <RangePicker
              value={selectedDateRange}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              style={{
                width: "100%",
                borderRadius: "16px",
              }}
              allowClear={true}
            />
          </Col>
        </Row>
      </Form>

      <Table
        bordered={false}
        className="mb-5"
        dataSource={activitylog}
        columns={resourceColumns}
        rowKey=""
        pagination={false}
        loading={isLoading}
        onRow={(record, index) => ({
          ref: index === activitylog.length - 30 ? lastElementRef : null,
        })}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  userList: getUserList(state),
});

export default connect(mapStateToProps, null)(ActivityLog);
